import React, { useState, useMemo } from 'react';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useSafes } from '../../hooks/useSafes';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useReportApi } from './useReportApi';
import { useDebouncedValue } from './useBanksReport';
import BankStatementSection from './BankStatementSection';

const SafesReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [safeFilter, setSafeFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const debouncedSearch = useDebouncedValue(searchTerm);

  const safesQO = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: safesData = [] } = useSafes(safesQO);

  const params = useMemo(() => ({
    safe: safeFilter,
    type: typeFilter,
    dateFrom,
    dateTo,
    search: debouncedSearch,
  }), [safeFilter, typeFilter, dateFrom, dateTo, debouncedSearch]);

  const { summary, results, isLoading, error, refetch } = useReportApi('/safes/report', params);

  const totalEntries = useMemo(
    () => results.reduce((sum: number, b: any) => sum + (b.entries?.length || 0), 0),
    [results]
  );

  const buildExportData = () => results.flatMap((block: any) =>
    (block.entries || []).map((e: any) => ({
      'Safe': block.safe?.name || '-',
      'Date': e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-',
      'Direction': e.direction,
      'Source': e.source,
      'Amount': e.amount,
      'Currency': e.currency || block.safe?.currency || '-',
      'Description': e.description || '-',
      'Cheque No': e.chequeNumber || '-',
      'Category': e.category || '-',
      'Reference': e.reference ? `${e.reference.type}: ${e.reference.description || e.reference.documentId}` : '-',
      'Balance Before': e.balanceBefore ?? '-',
      'Balance After': e.balanceAfter ?? '-',
    }))
  );

  const handleExport = () => exportToExcel(buildExportData(), `safes_statement_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Safe Boxes Statement Report');

  const handleClearFilters = () => {
    setSearchTerm('');
    setSafeFilter('all');
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const stats: StatCard[] = [
    { label: 'Total Safes', value: summary?.totalSafes ?? 0, color: 'blue' },
    { label: 'Total Balance', value: Number(summary?.totalBalance ?? 0).toLocaleString(), color: 'purple' },
    { label: 'Total Credit (In)', value: Number(summary?.totalCredit ?? 0).toLocaleString(), color: 'green' },
    { label: 'Total Debit (Out)', value: Number(summary?.totalDebit ?? 0).toLocaleString(), color: 'red' },
  ];

  const filters: FilterConfig[] = [
    { key: 'safe', label: 'Safe Box', type: 'select', value: safeFilter, onChange: setSafeFilter, options: [
      { value: 'all', label: 'All Safe Boxes' },
      ...(safesData as any[]).map((safe: any) => ({
        value: safe.documentId || String(safe.id),
        label: safe.name,
      })),
    ]},
    { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: setTypeFilter, options: [
      { value: 'all', label: 'All Entries' },
      { value: 'credit', label: 'Credit (Money In)' },
      { value: 'debit', label: 'Debit (Money Out)' },
    ]},
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateTo, onChange: setDateTo },
  ];

  if (error) return <ErrorState message={error} onRetry={refetch} entityName="safes report" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Safe Boxes Statement Report"
        description="Debit/credit statement per safe box: transactions, payments and manual balance adjustments"
      />
      <StatsCards stats={stats} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm}
        searchPlaceholder="Search description, cheque number, bill/invoice..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={totalEntries} filteredCount={totalEntries} entityName="statement entries"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
        <>
          {results.map((block: any) => (
            <BankStatementSection key={block.safe?.documentId || block.safe?.name} block={block} />
          ))}
          {results.length === 0 && (
            <div className="bg-white shadow rounded-lg text-center py-12">
              <p className="text-gray-500">No safe boxes match the selected filters</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SafesReport;
